import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { createLightRoom } from './lightRoomEngine';

// 🏡✨ The light room band (PORTED_HOME_HANDOFF_2026_09_25 §3b): a thin React
// shell over lightRoomEngine. Props carry state (clock, writing, sound);
// the ref carries moments (youSent / theySaid / welcomeBack). Every moment
// is a real one the page already knows about: nothing here is generated.
const PortedLightRoom = forwardRef(function PortedLightRoom({
  residentName = null,
  residentImage = null,
  minutes = 720,        // the owner's clock, minutes past midnight
  dayKey = null,        // "2026-09-25" on that clock (moon + shower nights)
  writing = false,      // ONLY the WS typing pair for the resident (§2 honesty)
  sound = false,        // per-device opt-in (D13)
  still = false,        // prefers-reduced-motion
  nightLight = false,   // 🌙 W3: the owner waved goodbye (the lamps drop to a night-light)
  lights = null,        // 💡 E2: the status `pictures` block ({ left, per_day }): his four lights; null = an older backend, all lit
}, ref) {
  const canvasRef = useRef(null);
  const roomRef = useRef(null);
  const soundSeen = useRef(false);

  useEffect(() => {
    const room = createLightRoom(canvasRef.current, { reducedMotion: still });
    roomRef.current = room;
    room.resize();
    room.start();
    const canvas = canvasRef.current;
    let ro = null;
    const onResize = () => room.resize();
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(onResize); ro.observe(canvas); }
    else window.addEventListener('resize', onResize);
    return () => {
      if (ro) ro.disconnect(); else window.removeEventListener('resize', onResize);
      room.destroy();
      roomRef.current = null;
    };
    // mount once; the effects below keep the room in step with its props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (roomRef.current) roomRef.current.setResident({ imageUrl: residentImage }); }, [residentImage]);
  useEffect(() => { if (roomRef.current) roomRef.current.setClock(minutes, dayKey); }, [minutes, dayKey]);
  useEffect(() => { if (roomRef.current) roomRef.current.setWriting(writing); }, [writing]);
  useEffect(() => { if (roomRef.current) roomRef.current.setStill(still); }, [still]);
  useEffect(() => { if (roomRef.current) roomRef.current.lampDown(!!nightLight); }, [nightLight]);
  // keyed on the two numbers: every status poll hands a new object, the lights only move when a count does
  useEffect(() => { if (roomRef.current) roomRef.current.setLights(lights); }, [lights && lights.left, lights && lights.per_day]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // the confirming bell rings only when the viewer flips it, never on load
    if (roomRef.current) roomRef.current.setSound(sound, { confirm: soundSeen.current });
    soundSeen.current = true;
  }, [sound]);

  useImperativeHandle(ref, () => ({
    youSent: () => roomRef.current && roomRef.current.youSent(),
    theySaid: (text) => roomRef.current && roomRef.current.theySaid(text),
    welcomeBack: () => roomRef.current && roomRef.current.welcomeBack(),
    // W2a, the home choice (§5c): driven by PortedStage from the status's `home`
    choiceStart: (doors) => roomRef.current && roomRef.current.choiceStart(doors),
    choiceReveal: (result, onEntered) => { if (roomRef.current) roomRef.current.choiceReveal(result, onEntered); else if (onEntered) onEntered(); },
    choiceCancel: () => roomRef.current && roomRef.current.choiceCancel(),
    // W3, the hatch (§5a): new ports only, driven by PortedStage
    hatch: (opts) => { if (roomRef.current) roomRef.current.hatch(opts); else if (opts && opts.onDone) opts.onDone(); },
    hatchLine: (text) => roomRef.current && roomRef.current.hatchLine(text),
  }), []);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={`${residentName || 'Your resident'}'s light room: floating lamps and flowers. Tap a lamp to light it, tap the dark to grow flowers.`}
      style={{ display: 'block', width: '100%', height: '100%', cursor: 'pointer', touchAction: 'manipulation' }}
    />
  );
});

export default PortedLightRoom;
